/* ==========================================================================
   FLEX SECTIONS  ·  behaviour

   One shared file for the flex-* family. Every routine is idempotent and
   scoped to a root element, so it can run on DOMContentLoaded and again on
   shopify:section:load when the theme editor re-renders a single section.

   Pointer-driven values are written to CSS custom properties and advanced by
   one shared rAF loop, so nothing re-renders the page per frame.
   ========================================================================== */
(function () {
  'use strict';

  // Sections each request this file, so guard against repeat execution.
  if (window.__fxSectionsReady) return;
  window.__fxSectionsReady = true;

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  /* ---------------------------------------------------------------- utils */
  function each(root, sel, fn) {
    Array.prototype.forEach.call(root.querySelectorAll(sel), fn);
  }
  // The flag is suffixed rather than written to el.dataset[key] directly: the
  // keys match the hook attributes, so done(wrap, 'fxMarquee') used to stamp
  // data-fx-marquee="1" and destroy the value the hook was carrying.
  function done(el, key) {
    var flag = key + 'Ready';
    if (el.dataset[flag] === '1') return true;
    el.dataset[flag] = '1';
    return false;
  }

  /* --------------------------------------------------- 1 · magnetic pull  */
  var springs = [];
  var raf = null;

  function loop() {
    var alive = false;
    for (var i = 0; i < springs.length; i++) {
      var s = springs[i];
      if (!s.el.isConnected) continue;
      var moving = false;
      for (var k = 0; k < s.keys.length; k++) {
        var key = s.keys[k];
        var d = s.target[key] - s.value[key];
        s.vel[key] = (s.vel[key] + d * 0.16) * 0.74;
        s.value[key] += s.vel[key];
        if (Math.abs(d) > 0.0015 || Math.abs(s.vel[key]) > 0.0015) moving = true;
      }
      if (!moving) {
        for (var j = 0; j < s.keys.length; j++) s.value[s.keys[j]] = s.target[s.keys[j]];
      } else {
        alive = true;
      }
      s.el.style.setProperty('--fx-mx', s.value.x.toFixed(2) + 'px');
      s.el.style.setProperty('--fx-my', s.value.y.toFixed(2) + 'px');
      s.el.style.setProperty('--fx-sc', s.value.sc.toFixed(4));
    }
    springs = springs.filter(function (s) { return s.el.isConnected; });
    raf = alive ? requestAnimationFrame(loop) : null;
  }
  function kick() { if (raf === null) raf = requestAnimationFrame(loop); }

  function magnetise(el) {
    var s = {
      el: el, keys: ['x', 'y', 'sc'],
      value: { x: 0, y: 0, sc: 1 }, vel: { x: 0, y: 0, sc: 0 }, target: { x: 0, y: 0, sc: 1 }
    };
    springs.push(s);
    var PULL = 0.34, MAX = 14;

    el.addEventListener('pointerenter', function (e) {
      if (e.pointerType === 'touch') return;
      el.classList.add('is-mag'); s.target.sc = 1.05; kick();
    });
    el.addEventListener('pointermove', function (e) {
      if (e.pointerType !== 'mouse') return;
      var r = el.getBoundingClientRect();
      var dx = (e.clientX - (r.left + r.width / 2)) * PULL;
      var dy = (e.clientY - (r.top + r.height / 2)) * PULL;
      var m = Math.sqrt(dx * dx + dy * dy);
      if (m > MAX) { dx = dx / m * MAX; dy = dy / m * MAX; }
      s.target.x = dx; s.target.y = dy; kick();
    });
    el.addEventListener('pointerleave', function () {
      el.classList.remove('is-mag');
      s.target.x = 0; s.target.y = 0; s.target.sc = 1; kick();
    });
  }

  function initButtons(root) {
    each(root, '.fx-btn', function (b) {
      if (done(b, 'fxBtn')) return;
      if (!b.querySelector('.fx-lbl')) {
        var lbl = document.createElement('span');
        lbl.className = 'fx-lbl';
        while (b.firstChild) lbl.appendChild(b.firstChild);
        b.appendChild(lbl);
      }
      if (fine && !reduce) magnetise(b);
    });
  }

  /* ------------------------------------ 2 · shimmer  ·  3 · border glow   */
  function decorate(root, sel, opts) {
    each(root, sel, function (el) {
      if (opts.shine) el.classList.add('fx-shine');
      if (opts.glow) el.classList.add('fx-glow');
      if (opts.dark) {
        if (opts.shine) el.classList.add('fx-shine--dark');
        if (opts.glow) el.classList.add('fx-glow--dark');
      }
    });
  }

  function initSurfaces(root) {
    decorate(root, '.fx-quote, .fx-pillar', { shine: true, glow: true });
    decorate(root, '.fx-panel', { glow: true });
    decorate(root, '.fx-show, .fx-order', { glow: true, dark: true });
    decorate(root, '.fx-clips .fx-media, .fx-band__pics figure, .fx-panel--media .fx-media',
      { shine: true, glow: true, dark: true });
  }

  var glowBound = false;
  function bindGlow() {
    if (glowBound || !fine || reduce) return;
    glowBound = true;
    document.addEventListener('pointermove', function (e) {
      if (e.pointerType !== 'mouse') return;
      var el = e.target.closest ? e.target.closest('.fx-glow') : null;
      if (!el) return;
      var r = el.getBoundingClientRect();
      el.style.setProperty('--fx-gx', (((e.clientX - r.left) / r.width) * 100).toFixed(1) + '%');
      el.style.setProperty('--fx-gy', (((e.clientY - r.top) / r.height) * 100).toFixed(1) + '%');
    }, { passive: true });
  }

  /* --------------------------------------- 4 · staggered character roll   */
  function rollify(link) {
    if (done(link, 'fxRoll')) return;
    var node = null;
    for (var i = 0; i < link.childNodes.length; i++) {
      var n = link.childNodes[i];
      if (n.nodeType === 3 && n.nodeValue.trim()) { node = n; break; }
    }
    if (!node) return;
    var text = node.nodeValue.trim();
    if (!text || text.length > 34) return;

    if (!link.getAttribute('aria-label')) link.setAttribute('aria-label', text);
    var roll = document.createElement('span');
    roll.className = 'fx-roll';
    for (var c = 0; c < text.length; c++) {
      var ch = document.createElement('span');
      ch.className = 'ch';
      ch.style.setProperty('--fx-d', (c * 18) + 'ms');
      var a = document.createElement('span'); a.className = 'a'; a.textContent = text[c];
      var b = document.createElement('span'); b.className = 'b'; b.textContent = text[c];
      b.setAttribute('aria-hidden', 'true');
      ch.appendChild(a); ch.appendChild(b);
      roll.appendChild(ch);
    }
    link.replaceChild(roll, node);

    if (!fine) {
      link.addEventListener('touchstart', function () {
        roll.classList.add('is-rolled');
        setTimeout(function () { roll.classList.remove('is-rolled'); }, 900);
      }, { passive: true });
    }
  }

  /* ----------------------------------------------- marquees and reveal    */
  // A marquee is paced in pixels per second rather than seconds per loop, so
  // its speed does not change when blocks are added or the type resizes. The
  // set is cloned until the track is comfortably wider than the viewport,
  // then shifted by exactly one set, which is what closes the loop seamlessly.
  var marquees = [];

  function measureMarquee(m) {
    // measured first-child to first-child, so the gap between sets is included
    var perSet = m.track.children.length / m.copies;
    var a = m.track.children[0];
    var b = m.track.children[perSet];
    var one = b ? b.offsetLeft - a.offsetLeft : m.track.scrollWidth / m.copies;
    if (!one) return;
    m.track.style.setProperty('--fx-shift', one.toFixed(2) + 'px');
    m.track.style.setProperty('--fx-run', (one / m.pace).toFixed(2) + 's');
  }

  function initMarquee(root) {
    each(root, '[data-fx-marquee]', function (wrap) {
      if (done(wrap, 'fxMarquee')) return;
      var track = wrap.querySelector('[data-fx-track]');
      if (!track || !track.children.length) return;

      var set = track.innerHTML;
      var copies = 1;
      // two copies close the loop; more only if one set is too narrow to fill
      // the screen, which would otherwise show the track reset mid-view
      while (copies < 2 || (track.scrollWidth < window.innerWidth * 2 && copies < 8)) {
        track.insertAdjacentHTML('beforeend', set);
        copies++;
      }
      Array.prototype.slice.call(track.children, track.children.length / copies)
        .forEach(function (n) { n.setAttribute('aria-hidden', 'true'); });

      var pace = parseFloat(wrap.getAttribute('data-fx-marquee')) || 24;
      var m = { track: track, copies: copies, pace: pace };
      marquees.push(m);
      measureMarquee(m);
      // Anton arrives after first paint and changes every width, so measure again
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(function () { measureMarquee(m); });
      }
    });
  }

  var remeasure = null;
  window.addEventListener('resize', function () {
    clearTimeout(remeasure);
    remeasure = setTimeout(function () {
      marquees.forEach(function (m) { if (m.track.isConnected) measureMarquee(m); });
    }, 200);
  }, { passive: true });

  function initReveal(root) {
    var targets = root.querySelectorAll('.fx-rv');
    if (!('IntersectionObserver' in window) || reduce) {
      Array.prototype.forEach.call(targets, function (el) { el.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        en.target.classList.add('is-in');
        if (!fine) {
          en.target.classList.add('is-swept');
          en.target.classList.add('is-live');
        }
        io.unobserve(en.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    Array.prototype.forEach.call(targets, function (el) { io.observe(el); });

    if (!fine && !reduce) {
      var io2 = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (!en.isIntersecting) return;
          en.target.classList.add('is-swept');
          en.target.classList.add('is-live');
          io2.unobserve(en.target);
        });
      }, { threshold: 0.35 });
      Array.prototype.forEach.call(root.querySelectorAll('.fx-shine, .fx-glow'),
        function (el) { io2.observe(el); });
    }
  }

  /* --------------------------------------------------- click-to-play      */
  function initPlay(root) {
    each(root, '[data-fx-play]', function (btn) {
      if (done(btn, 'fxPlay')) return;
      btn.addEventListener('click', function () {
        var media = btn.closest('.fx-media');
        if (!media) return;
        var vid = media.querySelector('video');
        var frame = media.getAttribute('data-fx-embed');
        if (vid) {
          if (vid.paused) { vid.play(); btn.style.opacity = '0'; }
          else { vid.pause(); btn.style.opacity = ''; }
          return;
        }
        if (frame) {
          var i = document.createElement('iframe');
          i.src = frame;
          i.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
          i.setAttribute('allowfullscreen', '');
          i.setAttribute('title', btn.getAttribute('aria-label') || 'Video');
          media.appendChild(i);
          btn.remove();
        }
      });
    });
  }

  /* --------------------------------------------------- request form       */
  function initForm(root) {
    each(root, '[data-fx-form]', function (form) {
      if (done(form, 'fxForm')) return;
      function fieldOf(el) { return el.closest('.fx-field'); }
      function validate(el) {
        var ok = true, v = (el.value || '').trim();
        if (el.hasAttribute('required') && !v) ok = false;
        if (ok && el.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v)) ok = false;
        var f = fieldOf(el);
        if (f) f.classList.toggle('is-bad', !ok);
        return ok;
      }
      each(form, 'input, select, textarea', function (el) {
        ['blur', 'input', 'change'].forEach(function (ev) {
          el.addEventListener(ev, function () {
            var f = fieldOf(el);
            if (f && f.classList.contains('is-bad')) validate(el);
          });
        });
      });
      form.addEventListener('submit', function (e) {
        var first = null, allOk = true;
        each(form, '[required]', function (el) {
          if (!validate(el)) { allOk = false; if (!first) first = el; }
        });
        if (!allOk) { e.preventDefault(); if (first) first.focus(); }
      });
    });
  }

  /* --------------------------------------------------------------- init   */
  function init(root) {
    root = root || document;
    initButtons(root);
    initSurfaces(root);
    bindGlow();
    initMarquee(root);
    initPlay(root);
    initForm(root);
    if (!reduce) each(root, '[data-fx-roll] a', rollify);
    initReveal(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  // the theme editor re-renders one section at a time
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:select', function (e) { init(e.target); });
})();
